import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, getAuthToken } from '../App';
import useLogger from '../hooks/useLogger';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

// A real LGS result is flagged purely by naming convention (examNumber containing
// "GERÇEK") — there's no dedicated backend field for it since the data model has no DB.
function isRealExam(examNumber) {
  return typeof examNumber === 'string' && examNumber.toUpperCase().includes('GERÇEK');
}

const VISIBLE_COLUMNS = 5.5;

// md breakpoint altında (telefon/küçük tablet) grafikler dikey istiflenir ve sütunlar daralır.
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

// Sayfadaki tüm kaydırmalı grafikler senkron kayar: biri kaydırılınca diğerleri aynı
// orana (scrollLeft / maxScroll) götürülür. Sütun genişlikleri farklı olsa bile sınav
// sayısı aynı olduğundan oransal eşleme sütunları hizalar.
const scrollSyncEls = new Set();
const scrollSyncPending = new WeakSet();
function syncScrollFrom(source) {
  const max = source.scrollWidth - source.clientWidth;
  const ratio = max > 0 ? source.scrollLeft / max : 0;
  scrollSyncEls.forEach((el) => {
    if (el === source) return;
    const m = el.scrollWidth - el.clientWidth;
    const target = ratio * m;
    if (Math.abs(el.scrollLeft - target) > 1) {
      scrollSyncPending.add(el);
      el.scrollLeft = target;
    }
  });
}

// Horizontally scrollable container: supports mouse-wheel (vertical wheel maps to
// horizontal scroll) and click-and-drag, in addition to native touch scrolling.
function HorizontalScrollArea({ children, style, className }) {
  const scrollRef = React.useRef(null);
  const dragState = React.useRef({ dragging: false, startX: 0, startScrollLeft: 0 });

  // React attaches onWheel as a passive listener, so e.preventDefault() inside it is a
  // silent no-op — the page would scroll vertically *and* the container horizontally at
  // once. A native, non-passive listener is required to properly hijack the wheel.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    // Scroll senkronizasyonu: programatik set'lerin tetiklediği scroll event'leri
    // (scrollSyncPending) geri beslemeye girmesin diye atlanır.
    scrollSyncEls.add(el);
    const onScroll = () => {
      if (scrollSyncPending.has(el)) {
        scrollSyncPending.delete(el);
        return;
      }
      syncScrollFrom(el);
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', onScroll);
      scrollSyncEls.delete(el);
    };
  }, []);

  const handleMouseDown = (e) => {
    dragState.current = { dragging: true, startX: e.pageX, startScrollLeft: scrollRef.current.scrollLeft };
  };
  const handleMouseMove = (e) => {
    if (!dragState.current.dragging) return;
    scrollRef.current.scrollLeft = dragState.current.startScrollLeft - (e.pageX - dragState.current.startX);
  };
  const endDrag = () => { dragState.current.dragging = false; };

  return (
    <div
      ref={scrollRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      className={`overflow-x-auto cursor-grab active:cursor-grabbing select-none ${className || ''}`}
      style={style}
    >
      {children}
    </div>
  );
}

// Renders a chart split into a frozen left panel (LGS + En İyi/Ortalama/En Kötü, if
// present) and a horizontally scrollable right panel (the deneme sınavları) — like an
// Excel frozen column. renderPinned/renderScrollable must return a recharts tree sized
// to 100%/100%; renderScrollable defaults to renderPinned when not given separately.
function PinnedScrollableChart({ pinnedData, scrollableData, columnWidth, height, renderPinned, renderScrollable }) {
  const isMobile = useIsMobile();
  const colW = isMobile ? Math.min(columnWidth, 80) : columnWidth;
  const chartHeight = isMobile ? Math.min(height, 320) : height;
  const scrollWidth = Math.max(scrollableData.length * colW, colW);
  const viewportWidth = colW * VISIBLE_COLUMNS;
  const renderScroll = renderScrollable || renderPinned;

  if (isMobile) {
    // Yan yana iki panel telefona sığmıyor: sabit sütunlar üstte, denemeler altta.
    return (
      <div className="space-y-3">
        {pinnedData.length > 0 && (
          <div className="overflow-x-auto border-b-2 border-dashed border-purple-300 pb-2">
            <div style={{ width: colW * pinnedData.length, minWidth: '100%', height: chartHeight }}>
              {renderPinned(pinnedData)}
            </div>
          </div>
        )}
        <HorizontalScrollArea style={{ width: '100%' }}>
          <div style={{ width: scrollWidth, height: chartHeight }}>
            {renderScroll(scrollableData)}
          </div>
        </HorizontalScrollArea>
      </div>
    );
  }

  return (
    <div className="flex items-stretch">
      {pinnedData.length > 0 && (
        <div className="flex-shrink-0 pr-3 mr-3 border-r-2 border-dashed border-purple-300">
          <div style={{ width: colW * pinnedData.length, height: chartHeight }}>
            {renderPinned(pinnedData)}
          </div>
        </div>
      )}
      <HorizontalScrollArea style={{ width: '100%', maxWidth: viewportWidth }}>
        <div style={{ width: scrollWidth, height: chartHeight }}>
          {renderScroll(scrollableData)}
        </div>
      </HorizontalScrollArea>
    </div>
  );
}

function StudentDashboard() {
  const { tcNo } = useParams();
  const navigate = useNavigate();
  const logger = useLogger();
  const [studentData, setStudentData] = useState(null);
  const [examStats, setExamStats] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [progressData, setProgressData] = useState([]);
  const [rankingChanges, setRankingChanges] = useState({ class: 0, school: 0, general: 0 });
  const [loading, setLoading] = useState(true);
  const [studentImage, setStudentImage] = useState(null);
  // Denemeler the user has excluded from charts/averages (e.g. one-off anomalous results).
  // Tracked as "unchecked" so the default (nothing in the set) means everything is included.
  const [uncheckedExams, setUncheckedExams] = useState(new Set());
  // Tüm Denemeler Özeti: column sort + inline rename/redate editing (persisted server-side).
  const [sortConfig, setSortConfig] = useState({ key: 'examDate', direction: 'asc' });
  const [editingExam, setEditingExam] = useState(null);
  const [editForm, setEditForm] = useState({ examNumber: '', examDate: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // Detaylı Ders Analizi has its own exam picker, independent of the page-level selector.
  const [detailExamSelection, setDetailExamSelection] = useState('');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    logger.trackPageVisit('student_detail');
  }, []);

  useEffect(() => {
    // Reset exclusions when switching to a different student.
    setUncheckedExams(new Set());
  }, [tcNo]);

  const toggleExamChecked = (examNumber) => {
    setUncheckedExams(prev => {
      const next = new Set(prev);
      if (next.has(examNumber)) next.delete(examNumber);
      else next.add(examNumber);
      return next;
    });
  };

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam && tcNo) {
      fetchStudentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam, tcNo]);

  useEffect(() => {
    // Check if student image exists
    if (tcNo) {
      // Images are served at /images (not /api/images); auth via ?token= since <img> can't send headers
      const baseURL = getApiUrl().replace('/api', '');
      const tokenParam = `?token=${encodeURIComponent(getAuthToken())}`;
      const img = new Image();
      img.src = `${baseURL}/images/${tcNo}.jpg${tokenParam}`;
      img.onload = () => setStudentImage(img.src);
      img.onerror = () => {
        // Try PNG format
        const img2 = new Image();
        img2.src = `${baseURL}/images/${tcNo}.png${tokenParam}`;
        img2.onload = () => setStudentImage(img2.src);
        img2.onerror = () => setStudentImage(null);
      };
    }
  }, [tcNo]);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/exams`);
      // Real LGS pinned first in the dropdown, denemeler follow in chronological order.
      const real = response.data.filter(e => isRealExam(e.examNumber));
      const denemeler = response.data.filter(e => !isRealExam(e.examNumber));
      setExams([...real, ...denemeler]);
      if (denemeler.length > 0) {
        // Default to the latest deneme rather than the real exam — the real exam's
        // rank/sözel/sayısal fields are N/A and would make the overview look broken.
        setSelectedExam(denemeler[denemeler.length - 1].examNumber);
      } else if (real.length > 0) {
        setSelectedExam(real[0].examNumber);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchStudentData = async () => {
    setLoading(true);
    try {
      // Fetch current exam data
      const response = await axios.get(`${getApiUrl()}/student/${tcNo}/exam/${selectedExam}`);
      setStudentData(response.data);
      
      // Set exam stats from the response (includes student-specific school/branch counts)
      if (response.data.statistics) {
        setExamStats(response.data.statistics);
      }
      
      // Fetch progress data to calculate ranking changes
      const progressResponse = await axios.get(`${getApiUrl()}/student/${tcNo}/progress`);
      const progressData = progressResponse.data;
      setProgressData(progressData);
      
      // Calculate ranking changes (skip for the real LGS result — MEB doesn't provide
      // class/school/general rank, so those fields are 0/not applicable there)
      if (progressData.length > 1 && !isRealExam(selectedExam)) {
        const denemeProgress = progressData.filter(exam => !isRealExam(exam.examNumber));
        const currentIndex = denemeProgress.findIndex(exam => exam.examNumber === selectedExam);
        if (currentIndex > 0) {
          const currentExam = denemeProgress[currentIndex];
          const previousExam = denemeProgress[currentIndex - 1];

          // Lower rank number is better, so if current < previous, it's an improvement (positive change)
          setRankingChanges({
            class: previousExam.classRank - currentExam.classRank,
            school: previousExam.schoolRank - currentExam.schoolRank,
            general: previousExam.generalRank - currentExam.generalRank
          });
        } else {
          setRankingChanges({ class: 0, school: 0, general: 0 });
        }
      } else {
        setRankingChanges({ class: 0, school: 0, general: 0 });
      }
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExamChange = (examNumber) => {
    setSelectedExam(examNumber);
  };

  const handleSort = (key) => {
    setSortConfig(prev => (
      prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    ));
  };

  const startEditExam = (exam) => {
    setEditingExam(exam.examNumber);
    setEditForm({ examNumber: exam.examNumber, examDate: exam.examDate });
  };

  const cancelEditExam = () => {
    setEditingExam(null);
  };

  const saveEditExam = async () => {
    setSavingEdit(true);
    try {
      const newExamNumber = editForm.examNumber.trim();
      const newExamDate = editForm.examDate.trim();
      await axios.put(`${getApiUrl()}/exams/${encodeURIComponent(editingExam)}`, {
        examNumber: newExamNumber,
        examDate: newExamDate
      });

      const wasSelected = editingExam === selectedExam;
      const wasDetailSelected = editingExam === (detailExamSelection || selectedExam);
      setEditingExam(null);
      setUncheckedExams(prev => {
        if (!prev.has(editingExam)) return prev;
        const next = new Set(prev);
        next.delete(editingExam);
        next.add(newExamNumber);
        return next;
      });

      // Refresh the dropdowns, then re-point them at the renamed exam if it was in use.
      await fetchExams();
      if (wasSelected) setSelectedExam(newExamNumber);
      if (wasDetailSelected) setDetailExamSelection(newExamNumber);

      // Refetch directly rather than via fetchStudentData()'s closured `selectedExam`,
      // which may still hold the pre-rename value at this point in the same tick.
      const progressResponse = await axios.get(`${getApiUrl()}/student/${tcNo}/progress`);
      setProgressData(progressResponse.data);

      const effectiveSelected = wasSelected ? newExamNumber : selectedExam;
      const examResponse = await axios.get(`${getApiUrl()}/student/${tcNo}/exam/${effectiveSelected}`);
      setStudentData(examResponse.data);
      if (examResponse.data.statistics) setExamStats(examResponse.data.statistics);
    } catch (error) {
      console.error('Error saving exam edit:', error);
      alert(error.response?.data?.message || 'Sınav güncellenemedi');
    } finally {
      setSavingEdit(false);
    }
  };

  const CircularProgress = ({ subject, data, color }) => {
    const netPercent = data.netPercent || 0;
    const circumference = 2 * Math.PI * 45;
    const strokeDashoffset = circumference - (netPercent / 100) * circumference;

    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex flex-col items-center">
        <div className="text-sm font-medium text-gray-700 mb-2">{subject}</div>
        <div className="relative w-32 h-32">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r="45"
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="45"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className={`transition-all duration-1000 ${color === 'red' ? 'text-red-500' : color === 'blue' ? 'text-blue-500' : color === 'green' ? 'text-green-500' : color === 'yellow' ? 'text-yellow-500' : color === 'purple' ? 'text-purple-500' : 'text-pink-500'}`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-2xl font-bold text-gray-800">{netPercent.toFixed(0)}%</div>
          </div>
        </div>
        <div className="mt-3 w-full space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-green-600">Doğru:</span>
            <span className="font-semibold">{data.true}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600">Yanlış:</span>
            <span className="font-semibold">{data.false}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Boş:</span>
            <span className="font-semibold">{data.empty}</span>
          </div>
          <div className="flex justify-between border-t pt-1">
            <span className="text-blue-600 font-semibold">Net:</span>
            <span className="font-bold">{data.net}</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading || !studentData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  const student = studentData.student;
  
  // Prepare subjects data
  const subjects = [
    { name: 'Turkce', displayName: 'Türkçe', color: 'red' },
    { name: 'Matematik', displayName: 'Matematik', color: 'blue' },
    { name: 'Fen ve Teknoloji', displayName: 'Fen', color: 'green' },
    { name: 'Inkilap Tarihi', displayName: 'Sosyal', color: 'yellow' },
    { name: 'ingilizce', displayName: 'İngilizce', color: 'purple' },
    { name: 'Din Kulturu ve Ahlak Bilgisi', displayName: 'Din', color: 'pink' }
  ];

  // Prepare radar chart data
  const radarData = subjects.map(({ name, displayName }) => ({
    subject: displayName,
    value: student.subjects[name]?.netPercent || 0,
    fullMark: 100
  }));

  // ── Denemeler feeding the trend charts/averages, honoring checkbox exclusions ──
  const denemeProgress = progressData.filter(exam => !isRealExam(exam.examNumber));
  const realProgress = progressData.filter(exam => isRealExam(exam.examNumber));
  const checkedDenemeProgress = denemeProgress.filter(exam => !uncheckedExams.has(exam.examNumber));

  const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
  const round2 = (n) => Math.round(n * 100) / 100;
  const round1 = (n) => Math.round(n * 10) / 10;

  const bestDeneme = checkedDenemeProgress.length > 0
    ? checkedDenemeProgress.reduce((best, e) => (e.totalScore > best.totalScore ? e : best))
    : null;
  const worstDeneme = checkedDenemeProgress.length > 0
    ? checkedDenemeProgress.reduce((worst, e) => (e.totalScore < worst.totalScore ? e : worst))
    : null;

  const averageDeneme = checkedDenemeProgress.length > 0 ? {
    examNumber: 'ORTALAMA',
    examDate: '',
    totalScore: round2(avg(checkedDenemeProgress.map(e => e.totalScore))),
    verbalScore: round2(avg(checkedDenemeProgress.map(e => e.verbalScore))),
    numericalScore: round2(avg(checkedDenemeProgress.map(e => e.numericalScore))),
    successRate: round2(avg(checkedDenemeProgress.map(e => e.successRate))),
    generalRank: null,
    subjects: subjects.reduce((acc, { name }) => {
      acc[name] = {
        true: round1(avg(checkedDenemeProgress.map(e => e.subjects?.[name]?.true || 0))),
        false: round1(avg(checkedDenemeProgress.map(e => e.subjects?.[name]?.false || 0))),
        empty: round1(avg(checkedDenemeProgress.map(e => e.subjects?.[name]?.empty || 0))),
      };
      return acc;
    }, {}),
  } : null;

  // Real LGS gets no MEB sözel/sayısal breakdown — estimate it from Begüm's own historical
  // sözel:toplam ratio across the checked denemeler (a data-driven estimate, not a guess).
  const realExamEstimated = realProgress.length > 0 ? (() => {
    const real = realProgress[0];
    const ratios = checkedDenemeProgress.filter(e => e.totalScore > 0).map(e => e.verbalScore / e.totalScore);
    const avgSozelRatio = ratios.length > 0 ? avg(ratios) : 0.5;
    const estSozel = round2(real.totalScore * avgSozelRatio);
    return { ...real, totalScore: round2(real.totalScore), verbalScore: estSozel, numericalScore: round2(real.totalScore - estSozel), generalRank: null };
  })() : null;

  // "En İyi - <deneme adı>" so it's clear which actual exam the system picked.
  const bestLabeled = bestDeneme ? { ...bestDeneme, examNumber: `EN İYİ - ${bestDeneme.examNumber}`, generalRank: null } : null;
  const worstLabeled = worstDeneme ? { ...worstDeneme, examNumber: `EN KÖTÜ - ${worstDeneme.examNumber}`, generalRank: null } : null;

  // LGS + En İyi/Ortalama/En Kötü: the four frozen columns shown next to the scrollable denemeler.
  const pinnedSpecialExams = [realExamEstimated, bestLabeled, averageDeneme, worstLabeled].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-800 flex items-center flex-shrink-0"
          >
            ← Ana Sayfa
          </button>
          <select
            value={selectedExam}
            onChange={(e) => handleExamChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm max-w-[65%] sm:max-w-none"
          >
            {exams.map((exam, idx) => (
              <React.Fragment key={exam.examNumber}>
                <option value={exam.examNumber}>
                  {isRealExam(exam.examNumber) ? `🏆 ${exam.examTitle}` : exam.examTitle}
                </option>
                {isRealExam(exam.examNumber) && idx + 1 < exams.length && !isRealExam(exams[idx + 1].examNumber) && (
                  <option disabled>──────────</option>
                )}
              </React.Fragment>
            ))}
          </select>
        </div>

        {/* Top Row: 2 Columns (50%-50%) */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          
          {/* Column 1: Student Info + Puan (Stacked) - 50% */}
          <div className="col-span-2 lg:col-span-1 space-y-4">
            {/* Begüm - Student Info */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="flex items-start space-x-4">
                {/* Left: Avatar + Okul No */}
                <div className="flex flex-col items-center flex-shrink-0">
                  {studentImage ? (
                    <img 
                      src={studentImage} 
                      alt={student.name}
                      className="w-24 h-24 rounded-full object-cover mb-2"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center mb-2">
                      <svg className="w-14 h-14 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-[10px] text-gray-500">Okul No</p>
                    <p className="text-base font-bold text-gray-800">{student.schoolNo}</p>
                  </div>
                </div>
                
                {/* Right: Info + Rankings */}
                <div className="flex-1 flex flex-col justify-between">
                  {/* Top: Name, School, Branch */}
                  <div className="mb-2">
                    <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-1">
                      {student.name}
                    </h2>
                    <p className="text-sm text-blue-600 font-medium mb-0.5">{student.schoolName}</p>
                    <p className="text-xs text-gray-600">{student.branch}</p>
                  </div>
                  
                  {/* Bottom: Rankings - Horizontal Row */}
                  {isRealExam(selectedExam) ? (
                    <div className="pt-2 border-t border-gray-200 text-center">
                      <div className="text-xs text-gray-500">
                        MEB gerçek LGS sonucunda şube/okul/genel sıralaması verilmiyor — bunun yerine
                        <span className="font-semibold text-purple-600"> Genel Yüzdelik Dilim: %{student.successRate}</span>
                      </div>
                    </div>
                  ) : (
                  <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-xs text-gray-500 mb-1">Şube</div>
                      <div className="flex items-center justify-center space-x-0.5">
                        <span className="text-2xl font-bold text-green-600">{student.classRank}</span>
                        {rankingChanges.class !== 0 && (
                          <span className={`text-base font-bold ${rankingChanges.class > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {rankingChanges.class > 0 ? '↑' : '↓'}
                            <span className="text-xs">{Math.abs(rankingChanges.class).toFixed(0)}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-xs text-gray-500 mb-1">Okul</div>
                      <div className="flex items-center justify-center space-x-0.5">
                        <span className="text-2xl font-bold text-blue-600">{student.schoolRank}</span>
                        {rankingChanges.school !== 0 && (
                          <span className={`text-base font-bold ${rankingChanges.school > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {rankingChanges.school > 0 ? '↑' : '↓'}
                            <span className="text-xs">{Math.abs(rankingChanges.school).toFixed(0)}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-xs text-gray-500 mb-1">Genel</div>
                      <div className="flex items-center justify-center space-x-0.5">
                        <span className="text-2xl font-bold text-purple-600">{student.generalRank}</span>
                        {rankingChanges.general !== 0 && (
                          <span className={`text-base font-bold ${rankingChanges.general > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {rankingChanges.general > 0 ? '↑' : '↓'}
                            <span className="text-xs">{Math.abs(rankingChanges.general).toFixed(0)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>

            {/* Puan Bars */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Puan</h3>
              <div className="space-y-3">
                {/* Genel Puan */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">Genel Puan</span>
                  </div>
                  <div className="relative h-10 bg-gray-100 rounded">
                    <div 
                      className="absolute top-0 left-0 h-full bg-purple-300 rounded"
                      style={{ width: '100%', opacity: 0.5 }}
                    ></div>
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded transition-all duration-1000"
                      style={{ width: `${(student.totalScore / 500) * 100}%` }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-between px-2 text-[10px] font-semibold">
                      <span className="text-white drop-shadow">{student.totalScore.toFixed(2)}</span>
                      <span className="text-gray-700">500</span>
                    </div>
                  </div>
                </div>

                {!isRealExam(selectedExam) && (
                <>
                {/* Sayisal */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">Sayısal</span>
                  </div>
                  <div className="relative h-6 bg-gray-100 rounded">
                    <div
                      className="absolute top-0 left-0 h-full bg-green-300 rounded"
                      style={{ width: '100%', opacity: 0.5 }}
                    ></div>
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 rounded transition-all duration-1000"
                      style={{ width: `${(student.numericalScore / 500) * 100}%` }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-start px-2 text-[10px] font-semibold">
                      <span className="text-white drop-shadow">{student.numericalScore.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Sozel */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">Sözel</span>
                  </div>
                  <div className="relative h-6 bg-gray-100 rounded">
                    <div
                      className="absolute top-0 left-0 h-full bg-blue-300 rounded"
                      style={{ width: '100%', opacity: 0.5 }}
                    ></div>
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded transition-all duration-1000"
                      style={{ width: `${(student.verbalScore / 500) * 100}%` }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-start px-2 text-[10px] font-semibold">
                      <span className="text-white drop-shadow">{student.verbalScore.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                </>
                )}
              </div>
            </div>
          </div>

          {/* Column 2: Exam Stats - 50% */}
          <div className="col-span-2 lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4 h-full flex flex-col">
              {/* Top: Deneme Sınavı Header and Participant Stats */}
              <div className="mb-3">
                <div className="mb-2">
                  <h3 className="text-base font-semibold text-gray-900">Deneme Sınavı: {selectedExam}</h3>
                  <p className="text-xs text-gray-600">{studentData.examInfo.examDate}</p>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {examStats && (
                    <>
                      <div className="bg-blue-50 rounded p-2 text-center">
                        <div className="text-[10px] text-blue-600">Total Katılımcı</div>
                        <div className="text-xl font-bold text-blue-700">{examStats.totalParticipants}</div>
                      </div>
                      <div className="bg-green-50 rounded p-2 text-center">
                        <div className="text-[10px] text-green-600">Okul Katılımcı</div>
                        <div className="text-xl font-bold text-green-700">{examStats.schoolParticipants}</div>
                      </div>
                      <div className="bg-purple-50 rounded p-2 text-center">
                        <div className="text-[10px] text-purple-600">Şube Katılımcı</div>
                        <div className="text-xl font-bold text-purple-700">{examStats.branchParticipants}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Bottom: Ortalama with Subject Grid with Emojis */}
              {examStats && examStats.subjectAverages && (
                <div className="bg-white border-2 border-gray-200 rounded-lg p-3 flex-1">
                  <div className="text-center mb-2">
                    <div className="text-xs text-orange-600 font-medium">Ortalama</div>
                    <div className="text-3xl font-bold text-orange-600">{examStats.averageScore}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(examStats.subjectAverages).map(([key, value]) => {
                      const subjectInfo = {
                        Turkce: { name: 'Türkçe', emoji: '📝', bg: 'bg-red-50' },
                        Matematik: { name: 'Matematik', emoji: '🔢', bg: 'bg-blue-50' },
                        Fen: { name: 'Fen', emoji: '🔬', bg: 'bg-green-50' },
                        Sosyal: { name: 'Sosyal', emoji: '🌍', bg: 'bg-yellow-50' },
                        Ingilizce: { name: 'İngilizce', emoji: '🗣️', bg: 'bg-purple-50' },
                        Din: { name: 'Din', emoji: '📿', bg: 'bg-pink-50' }
                      };
                      const info = subjectInfo[key] || { name: key, emoji: '📚', bg: 'bg-gray-50' };
                      return (
                        <div key={key} className={`${info.bg} rounded-lg p-2 flex flex-col items-center justify-center`}>
                          <div className="text-xl mb-0.5">{info.emoji}</div>
                          <div className="text-[9px] text-gray-600 mb-0.5">{info.name}</div>
                          <div className="text-base font-bold text-gray-800">{value}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second Row: Ders Performansı (60%) + Analiz (40%) */}
        <div className="grid grid-cols-10 gap-6 mb-6">
          {/* Left: Ders Performansı Circles - 60% */}
          <div className="col-span-10 lg:col-span-6 bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ders Performansı</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {subjects.map(({ name, displayName, color }) => {
                const subjectData = student.subjects[name];
                return subjectData ? (
                  <CircularProgress
                    key={name}
                    subject={displayName}
                    data={subjectData}
                    color={color}
                  />
                ) : null;
              })}
            </div>
          </div>

          {/* Right: Analiz (Radar Chart + Error Chart) - 40% */}
          <div className="col-span-10 lg:col-span-4">
            <div className="bg-white rounded-lg shadow-lg p-4 h-full flex flex-col">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Analiz</h3>
              
              {/* Charts Container - Equal 50-50 split. min-h şart: mobilde kartlar alt alta
                  dizilince h-full/flex-1 zinciri yükseklik üretmez ve grafikler 0px'e çöker. */}
              <div className="flex-1 flex flex-col">
                {/* Radar Chart - 50% */}
                <div className="flex-1 min-h-[240px] mb-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e5e7eb" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: '#6b7280', fontSize: 9 }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 8 }} angle={90} />
                      <Radar name="Net %" dataKey="value" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.6} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Error Bar Chart (Yanlış + Boş) - 50% */}
                <div className="flex-1 min-h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={subjects.map(({ name, displayName }) => {
                        const subjectData = student.subjects[name];
                        return {
                          name: displayName,
                          yanlis: subjectData?.false || 0,
                          bos: subjectData?.empty || 0
                        };
                      })}
                      layout="vertical"
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 9 }} />
                      <YAxis dataKey="name" type="category" tick={{ fill: '#6b7280', fontSize: 9 }} width={60} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Bar dataKey="yanlis" stackId="a" fill="#ef4444" name="Yanlış" barSize={15} />
                      <Bar dataKey="bos" stackId="a" fill="#d97706" name="Boş" barSize={15} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Genel Sıralama & Yüzdelik Dilim Chart */}
        {denemeProgress.length > 0 && (() => {
          const COLUMN_WIDTH = 110;
          if (checkedDenemeProgress.length === 0) {
            return (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Genel Sıralama & Yüzdelik Dilim</h3>
                <p className="text-sm text-gray-500 mt-4">Grafiği görmek için aşağıdan en az bir deneme seçili olmalı.</p>
              </div>
            );
          }
          const allForDomain = [...checkedDenemeProgress, ...pinnedSpecialExams];
          const allRanks = allForDomain.map(e => e.generalRank).filter(r => r !== null && r !== undefined);
          const rankMax = allRanks.length > 0 ? Math.max(...allRanks) : 100;
          const allSuccessRates = allForDomain.map(e => e.successRate).filter(v => v !== null && v !== undefined);
          const maxSuccessRate = allSuccessRates.length > 0 ? Math.max(...allSuccessRates) : 1;
          const yuzdelikDilimMax = Math.ceil(maxSuccessRate * 1.5);

          // Eksen çubukları ve legend yalnızca çok sınavlı (kaydırmalı) grafikte; sabit
          // panelde eksen gizli ama domain aynı tutulur ki iki grafik aynı skalada çizilsin.
          const renderPinned = (data) => (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.map(exam => ({ name: exam.examNumber, ydPercent: exam.successRate }))}
                margin={{ top: 40, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" orientation="top" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={{ stroke: '#6b7280' }} />
                <YAxis reversed={true} domain={[0, yuzdelikDilimMax]} hide />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb' }} />
                <Bar dataKey="ydPercent" fill="#3b82f6" fillOpacity={0.6} name="Yüzdelik Dilim" barSize={20} label={{ position: 'top', fill: '#3b82f6', fontSize: 10 }} />
              </ComposedChart>
            </ResponsiveContainer>
          );

          const renderScrollable = (data) => (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.map(exam => ({ name: exam.examNumber, rank: exam.generalRank, ydPercent: exam.successRate }))}
                margin={{ top: 40, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" orientation="top" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#6b7280' }} />
                <YAxis yAxisId="left" reversed={true} domain={[0, rankMax]} tick={{ fill: '#8b5cf6', fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" reversed={true} domain={[0, yuzdelikDilimMax]} tick={{ fill: '#3b82f6', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb' }} />
                <Legend />
                <Bar yAxisId="right" dataKey="ydPercent" fill="#3b82f6" fillOpacity={0.6} name="Yüzdelik Dilim" barSize={20} />
                <Line yAxisId="left" type="monotone" dataKey="rank" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', r: 5 }} name="Genel Sıralama" connectNulls={false} label={{ position: 'bottom', fill: '#8b5cf6', fontSize: 13, fontWeight: 'bold', offset: 10 }} />
              </ComposedChart>
            </ResponsiveContainer>
          );

          return (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Genel Sıralama & Yüzdelik Dilim</h3>
              <p className="text-[11px] text-gray-500 mb-3">LGS, En İyi, Ortalama ve En Kötü sütunlarında sıralama gösterilmez — sadece Yüzdelik Dilim.</p>
              <PinnedScrollableChart
                pinnedData={pinnedSpecialExams}
                scrollableData={checkedDenemeProgress}
                columnWidth={COLUMN_WIDTH}
                height={450}
                renderPinned={renderPinned}
                renderScrollable={renderScrollable}
              />
            </div>
          );
        })()}

        {/* Haftalık Puan Değişimi Chart */}
        {denemeProgress.length > 0 && (() => {
          const COLUMN_WIDTH = 110;
          if (checkedDenemeProgress.length === 0) {
            return (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Haftalık Puan Değişimi</h3>
                <p className="text-sm text-gray-500 mt-4">Grafiği görmek için aşağıdan en az bir deneme seçili olmalı.</p>
              </div>
            );
          }
          const allForDomain = [...checkedDenemeProgress, ...pinnedSpecialExams];
          const allScores = allForDomain.map(exam => exam.totalScore);
          const allVerbalScores = allForDomain.map(exam => exam.verbalScore);
          const allNumericalScores = allForDomain.map(exam => exam.numericalScore);

          const toplamMin = Math.floor(Math.min(...allScores) / 1.25);
          const toplamMax = Math.ceil(Math.max(...allScores) + 5);
          const sozelSayisalMin = Math.floor(Math.min(...allVerbalScores, ...allNumericalScores) / 2);
          const sozelSayisalMax = Math.ceil(Math.max(...allVerbalScores, ...allNumericalScores) * 1.7);

          // Eksenler ve legend çok sınavlı (kaydırmalı) grafikte; sabit panelde eksenler
          // gizli ama domainler aynı — iki grafik aynı skala ve aynı bar boyutunda çizilir.
          // Sabit panelde eksen/marj yükü kalktığı için toplam genişlik değişmeden
          // sınav grupları arası açıldı (barSize 30 → 20).
          const renderPinned = (data) => (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.map(exam => ({ name: exam.examNumber, sozel: exam.verbalScore, sayisal: exam.numericalScore, toplam: exam.totalScore }))}
                margin={{ top: 20, right: 5, left: 5, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis yAxisId="left" domain={[sozelSayisalMin, sozelSayisalMax]} hide />
                <YAxis yAxisId="right" orientation="right" domain={[toplamMin, toplamMax]} hide />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb' }} />
                <Bar yAxisId="left" dataKey="sozel" fill="#1e40af" name="Sözel Puan" barSize={20} label={{ position: 'top', fill: '#1e40af', fontSize: 10 }} />
                <Bar yAxisId="left" dataKey="sayisal" fill="#dc2626" name="Sayısal Puan" barSize={20} label={{ position: 'top', fill: '#dc2626', fontSize: 10 }} />
                <Line yAxisId="right" type="monotone" dataKey="toplam" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 5 }} name="Toplam Puan" label={{ position: 'top', fill: '#10b981', fontSize: 11 }} />
              </ComposedChart>
            </ResponsiveContainer>
          );

          const renderScrollable = (data) => (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.map(exam => ({ name: exam.examNumber, sozel: exam.verbalScore, sayisal: exam.numericalScore, toplam: exam.totalScore }))}
                margin={{ top: 20, right: 5, left: 5, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis yAxisId="left" domain={[sozelSayisalMin, sozelSayisalMax]} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" domain={[toplamMin, toplamMax]} tick={{ fill: '#10b981', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb' }} />
                <Legend />
                <Bar yAxisId="left" dataKey="sozel" fill="#1e40af" name="Sözel Puan" barSize={20} label={{ position: 'top', fill: '#1e40af', fontSize: 10 }} />
                <Bar yAxisId="left" dataKey="sayisal" fill="#dc2626" name="Sayısal Puan" barSize={20} label={{ position: 'top', fill: '#dc2626', fontSize: 10 }} />
                <Line yAxisId="right" type="monotone" dataKey="toplam" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 5 }} name="Toplam Puan" label={{ position: 'top', fill: '#10b981', fontSize: 11 }} />
              </ComposedChart>
            </ResponsiveContainer>
          );

          return (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Haftalık Puan Değişimi</h3>
              {realProgress.length > 0 && (
                <p className="text-[11px] text-gray-500 mb-3">LGS'nin Sözel/Sayısal ayrımı MEB'den gelmiyor — geçmiş deneme oranından tahmin edilmiştir.</p>
              )}
              <PinnedScrollableChart
                pinnedData={pinnedSpecialExams}
                scrollableData={checkedDenemeProgress}
                columnWidth={COLUMN_WIDTH}
                height={450}
                renderPinned={renderPinned}
                renderScrollable={renderScrollable}
              />
            </div>
          );
        })()}

        {/* Haftalık Ders Bazlı Cevap Dağılımı - 6 Subject Charts in 2x3 Grid */}
        {denemeProgress.length > 0 && (() => {
          const SUBJECT_CHARTS = [
            { label: 'Türkçe', key: 'Turkce', domain: [10, 20] },
            { label: 'Matematik', key: 'Matematik', domain: [10, 20] },
            { label: 'Sosyal', key: 'Inkilap Tarihi', domain: [5, 10] },
            { label: 'Fen', key: 'Fen ve Teknoloji', domain: [10, 20] },
            { label: 'İngilizce', key: 'ingilizce', domain: [5, 10] },
            { label: 'Din', key: 'Din Kulturu ve Ahlak Bilgisi', domain: [5, 10] },
          ];
          const COLUMN_WIDTH = 90;

          return (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Haftalık Ders Bazlı Cevap Dağılımı</h3>

              {/* One subject per row — more breathing room than a cramped 2-column grid */}
              <div className="space-y-4">
                {SUBJECT_CHARTS.map(({ label, key, domain }) => {
                  const toChartData = (data) => data.map(exam => ({
                    name: isRealExam(exam.examNumber) ? 'LGS' : exam.examNumber,
                    dogru: exam.subjects?.[key]?.true || 0,
                    yanlis: exam.subjects?.[key]?.false || 0,
                    bos: exam.subjects?.[key]?.empty || 0,
                  }));
                  const renderChart = (data) => (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={toChartData(data)} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <YAxis domain={domain} tick={{ fill: '#6b7280', fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', fontSize: '11px' }} />
                        <Bar dataKey="dogru" stackId="a" fill="#10b981" barSize={30} />
                        <Bar dataKey="yanlis" stackId="a" fill="#dc2626" barSize={30} />
                        <Bar dataKey="bos" stackId="a" fill="#9ca3af" barSize={30} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  );
                  return (
                    <div key={key} className="border border-gray-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">{label}</h4>
                      {checkedDenemeProgress.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-8">En az bir deneme seçili olmalı.</p>
                      ) : (
                        <PinnedScrollableChart
                          pinnedData={pinnedSpecialExams}
                          scrollableData={checkedDenemeProgress}
                          columnWidth={COLUMN_WIDTH}
                          height={220}
                          renderPinned={renderChart}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Tüm Denemeler Özeti Table */}
        {progressData.length > 0 && (() => {
          const parseDateForSort = (str) => {
            const parts = String(str).split('.');
            return parts.length === 3 ? new Date(parts[2], parts[1] - 1, parts[0]).getTime() : 0;
          };
          const SORT_ACCESSORS = {
            examNumber: e => e.examNumber,
            examDate: e => parseDateForSort(e.examDate),
            totalScore: e => e.totalScore,
            generalRank: e => (e.generalRank === null || e.generalRank === undefined ? -Infinity : e.generalRank),
            totalNet: e => e.totals.totalNet || 0,
          };
          const sortedDenemeRows = denemeProgress.slice().sort((a, b) => {
            const accessor = SORT_ACCESSORS[sortConfig.key] || SORT_ACCESSORS.examDate;
            const av = accessor(a), bv = accessor(b);
            if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
            if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
          });
          const rows = [...realProgress, ...sortedDenemeRows];

          const SortHeader = ({ sortKey, children, align = 'left' }) => (
            <th
              onClick={() => handleSort(sortKey)}
              className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700`}
            >
              {children}{' '}
              {sortConfig.key === sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
            </th>
          );

          return (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Tüm Denemeler Özeti</h3>
                <div className="text-xs space-x-3">
                  <button onClick={() => setUncheckedExams(new Set())} className="text-blue-600 hover:underline">Tümünü seç</button>
                  <button onClick={() => setUncheckedExams(new Set(denemeProgress.map(e => e.examNumber)))} className="text-blue-600 hover:underline">Tümünü kaldır</button>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                İşareti kaldırdığın denemeler grafiklerden ve En İyi/Ortalama/En Kötü hesaplarından çıkarılır (örn. olağandışı bir sonuç için). Başlıklara tıklayarak sıralayabilir, kalem ikonuyla deneme adı/tarihini düzenleyebilirsin (tüm öğrencileri etkiler, kalıcıdır).
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Dahil</th>
                      <SortHeader sortKey="examNumber">Deneme</SortHeader>
                      <SortHeader sortKey="examDate">Tarih</SortHeader>
                      <SortHeader sortKey="totalScore" align="center">Toplam Puan</SortHeader>
                      <SortHeader sortKey="generalRank" align="center">Genel Sıralama</SortHeader>
                      <SortHeader sortKey="totalNet" align="center">Toplam Net</SortHeader>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((exam) => {
                      const excluded = !isRealExam(exam.examNumber) && uncheckedExams.has(exam.examNumber);
                      const isEditing = editingExam === exam.examNumber;
                      return (
                        <tr
                          key={exam.examNumber}
                          className={
                            (excluded ? 'opacity-40 ' : '') +
                            (exam.examNumber === selectedExam ? 'bg-blue-50 ' : 'hover:bg-gray-50 ') +
                            (isRealExam(exam.examNumber) ? 'bg-purple-50 border-b-2 border-purple-300' : '')
                          }
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            {!isRealExam(exam.examNumber) && (
                              <input
                                type="checkbox"
                                checked={!excluded}
                                onChange={() => toggleExamChecked(exam.examNumber)}
                                className="w-4 h-4 accent-blue-600 cursor-pointer"
                              />
                            )}
                          </td>
                          {isEditing ? (
                            <>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={editForm.examNumber}
                                  onChange={(e) => setEditForm(f => ({ ...f, examNumber: e.target.value }))}
                                  className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  placeholder="gg.aa.yyyy"
                                  value={editForm.examDate}
                                  onChange={(e) => setEditForm(f => ({ ...f, examDate: e.target.value }))}
                                  className="w-28 px-2 py-1 border border-blue-300 rounded text-sm"
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {isRealExam(exam.examNumber) ? `🏆 ${exam.examNumber}` : exam.examNumber}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{exam.examDate}</td>
                            </>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-blue-600">{exam.totalScore.toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-green-600">{isRealExam(exam.examNumber) ? '—' : exam.generalRank}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-purple-600">{(exam.totals.totalNet ?? 0).toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            {isEditing ? (
                              <div className="flex items-center justify-center space-x-2">
                                <button
                                  onClick={saveEditExam}
                                  disabled={savingEdit}
                                  title="Kaydet"
                                  className="text-green-600 hover:text-green-800 disabled:opacity-50"
                                >
                                  ✓
                                </button>
                                <button onClick={cancelEditExam} title="Vazgeç" className="text-red-500 hover:text-red-700">
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEditExam(exam)}
                                title="Deneme adı/tarihini düzenle"
                                className="text-gray-400 hover:text-blue-600"
                              >
                                ✏️
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Detaylı Ders Analizi Table — its own exam picker, independent of the page-level one */}
        {(() => {
          const effectiveDetailExam = detailExamSelection || selectedExam;
          const detailExam = progressData.find(e => e.examNumber === effectiveDetailExam);
          if (!detailExam) return null;
          return (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  Detaylı Ders Analizi - {detailExam.examNumber} ({detailExam.examDate})
                </h3>
                <select
                  value={effectiveDetailExam}
                  onChange={(e) => setDetailExamSelection(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                >
                  {[...realProgress, ...denemeProgress].map(exam => (
                    <option key={exam.examNumber} value={exam.examNumber}>
                      {isRealExam(exam.examNumber) ? `🏆 ${exam.examNumber}` : exam.examNumber} ({exam.examDate})
                    </option>
                  ))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ders</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-green-500 uppercase">Doğru</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-red-500 uppercase">Yanlış</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Boş</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-blue-500 uppercase">Net</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-purple-500 uppercase">Net %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(detailExam.subjects).map(([subjectName, subjectData]) => {
                      const displayName = subjectName === 'ingilizce' ? 'İngilizce' :
                                        subjectName === 'Turkce' ? 'Türkçe' :
                                        subjectName;
                      return (
                        <tr key={subjectName} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{displayName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-green-600">{subjectData.true}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-red-600">{subjectData.false}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-gray-600">{subjectData.empty}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="font-semibold text-blue-600">{subjectData.net.toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              %{subjectData.netPercent.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">TOPLAM</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-green-600">{detailExam.totals.totalTrue}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-red-600">{detailExam.totals.totalFalse}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">{detailExam.totals.totalEmpty}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-blue-600">{(detailExam.totals.totalNet ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}

export default StudentDashboard;
